import { BuildingAreaType_Enum, type ProjectVersion_Insert_Input } from "@/graphql";
import type { BuildingUseType } from "../types/BuildingUseType";
import type { HeatAreaType } from "../types/HeatAreaType";
import type { ReferencedAreasType } from "../types/ReferencedAreasType";
import { byGrossAreaDescending, grossArea } from "../utils/buildingAreaTable";

// The pure half of the building-area rail: the two rows a save sends and the trail row that describes
// it. Kept apart from the write itself so it can be tested without loading the Apollo client, the way
// referenceAreaWriteInput.ts is.

/** The six numbers the project holds, straight off `emissionCalculationAssumptions`, nulls and all. */
export interface ProjectBuildingAreasWriteInput {
  projectId: string;
  /** referencedAreas.areaAboveGround */
  areaAboveGround: number | null;
  /** referencedAreas.floorsAboveGround */
  floorsAboveGround: number | null;
  /** heatArea.heatedFloorAreaAboveGround */
  heatedAreaAboveGround: number | null;
  /** referencedAreas.areaBelowGround */
  areaBelowGround: number | null;
  /** referencedAreas.basementFloors */
  basementFloors: number | null;
  /** heatArea.heatedBasementArea */
  heatedBasementArea: number | null;
  /** buildingTypeEnum: a single use's two rows carry it. */
  buildingTypeId?: number | null;
  /** The uses of a mixed-use project, each stored as its own pair of rows; empty for a single use. */
  uses?: BuildingUseType[];
}

/**
 * The part of a project this rail reads, named structurally so both project types fit: the saved
 * `BuildingProjectType` and the create wizard's own model, which has no `id` yet.
 */
export interface ProjectBuildingAreaSource {
  id?: string;
  buildingTypeEnum?: number | string | null;
  buildingUses?: BuildingUseType[];
  emissionCalculationAssumptions?: {
    referencedAreas?: ReferencedAreasType;
    heatArea?: HeatAreaType | null;
  };
}

/** A single use is no split at all: it is stored as the project's own pair of rows. */
export const isMixedUse = (uses: BuildingUseType[] | null | undefined): uses is BuildingUseType[] => (uses?.length ?? 0) >= 2;

/** What a save stores per use: none for a single-use project, else every row as the table holds it. */
export const buildingUsesToSave = (project: { buildingUses?: BuildingUseType[] }): BuildingUseType[] => {
  if (!isMixedUse(project.buildingUses)) return [];
  return project.buildingUses.map((use) => ({
    buildingTypeId: use.buildingTypeId === null ? null : Number(use.buildingTypeId),
    area: grossArea(use),
    areaAboveGround: use.areaAboveGround ?? null,
    floorsAboveGround: use.floorsAboveGround ?? null,
    heatedAreaAboveGround: use.heatedAreaAboveGround ?? null,
    areaBelowGround: use.areaBelowGround ?? null,
    floorsBelowGround: use.floorsBelowGround ?? null,
    heatedAreaBelowGround: use.heatedAreaBelowGround ?? null,
  }));
};

const toTypeId = (value: number | string | null | undefined): number | null =>
  value === null || value === undefined || value === "" ? null : Number(value);

/**
 * Pick the six numbers off a project.
 *
 * One extractor rather than one per caller, because the six live in two different objects
 * (`referencedAreas` holds the gross areas and the floor counts, `heatArea` the heated ones) and
 * every reader has to agree about which field feeds which column. The settings page's edited-marker
 * compares the ROWS this produces, so a reader that picked a field differently from the writer would
 * mark a section edited by disagreeing with the save rather than by anything the reader did
 * (RTLCA-5782).
 *
 * Tolerant of a missing project so a fingerprint can be taken before one has loaded: every absent
 * field is `null`, which is what an unanswered one is anyway.
 */
export const buildingAreasOf = (project: ProjectBuildingAreaSource | null | undefined): ProjectBuildingAreasWriteInput => {
  const assumptions = project?.emissionCalculationAssumptions;
  const referencedAreas = assumptions?.referencedAreas;
  const heatArea = assumptions?.heatArea;

  return {
    projectId: project?.id ?? "",
    areaAboveGround: referencedAreas?.areaAboveGround ?? null,
    floorsAboveGround: referencedAreas?.floorsAboveGround ?? null,
    heatedAreaAboveGround: heatArea?.heatedFloorAreaAboveGround ?? null,
    areaBelowGround: referencedAreas?.areaBelowGround ?? null,
    basementFloors: referencedAreas?.basementFloors ?? null,
    heatedBasementArea: heatArea?.heatedBasementArea ?? null,
    buildingTypeId: toTypeId(project?.buildingTypeEnum),
    uses: project ? buildingUsesToSave(project) : [],
  };
};

export interface ProjectBuildingAreaRowInput {
  projectId: string;
  /** Null only before a building type is picked; Hasura then stamps the project's own. */
  buildingTypeId: number | null;
  buildingAreaType: BuildingAreaType_Enum;
  area: number;
  floors: number;
  heatedArea: number;
}

/**
 * Always exactly two rows, one per BuildingAreaType_Enum value, because the API's own writer stores
 * them that way and the wrapper reads them back by looking each type up
 * (projectWrapperFromHasura: `buildingAreas.find(...)`). A project with only the above-ground row
 * would read `null` for its basement and be indistinguishable from one that has no rows at all.
 *
 * `?? 0` on all three numbers: area, floors and heated_area are NOT NULL in Postgres
 * (1727960503617_create_table_public_project_building_above_ground), so there is no representation of
 * "unset" for these columns anywhere in the system. 0 is exactly what the REST path already stores —
 * `HasuraProjectBuildingAreaRow` in realtimelca-api takes `double Area, int Floors, double HeatedArea`
 * — so the value written is unchanged; only which client writes it moves.
 *
 * `referencedAreas.totalArea` is deliberately absent: it is `project_area_calculate`'s sum of these
 * rows, not an input.
 *
 * A mixed-use project stores that pair once per building use instead, largest gross
 * area first; its project-level figures are the sums Hasura and the API add up from them.
 */
export const buildingAreaRows = (input: ProjectBuildingAreasWriteInput): ProjectBuildingAreaRowInput[] =>
  isMixedUse(input.uses) ? useRows(input.projectId, input.uses) : projectRows(input);

const projectRows = (input: ProjectBuildingAreasWriteInput): ProjectBuildingAreaRowInput[] => [
  {
    projectId: input.projectId,
    buildingTypeId: input.buildingTypeId ?? null,
    buildingAreaType: BuildingAreaType_Enum.above_ground,
    area: input.areaAboveGround ?? 0,
    floors: input.floorsAboveGround ?? 0,
    heatedArea: input.heatedAreaAboveGround ?? 0,
  },
  {
    projectId: input.projectId,
    buildingTypeId: input.buildingTypeId ?? null,
    buildingAreaType: BuildingAreaType_Enum.below_ground,
    area: input.areaBelowGround ?? 0,
    floors: input.basementFloors ?? 0,
    heatedArea: input.heatedBasementArea ?? 0,
  },
];

// A row without a type is dropped to keep the insert valid; validation keeps one from being saved.
const useRows = (projectId: string, uses: BuildingUseType[]): ProjectBuildingAreaRowInput[] =>
  byGrossAreaDescending(uses).flatMap((use) =>
    use.buildingTypeId === null
      ? []
      : [
          {
            projectId,
            buildingTypeId: Number(use.buildingTypeId),
            buildingAreaType: BuildingAreaType_Enum.above_ground,
            area: use.areaAboveGround ?? 0,
            floors: use.floorsAboveGround ?? 0,
            heatedArea: use.heatedAreaAboveGround ?? 0,
          },
          {
            projectId,
            buildingTypeId: Number(use.buildingTypeId),
            buildingAreaType: BuildingAreaType_Enum.below_ground,
            area: use.areaBelowGround ?? 0,
            floors: use.floorsBelowGround ?? 0,
            heatedArea: use.heatedAreaBelowGround ?? 0,
          },
        ]
  );

/**
 * The same six numbers `buildingAreaRows` writes, put back into the two objects the REST
 * calculation-assumptions PUT carries them in.
 *
 * The PUT is the fallback rail for these fields, and its contract has no null in it:
 * `CreateOrUpdateReferencedAreasRequest` in realtimelca-api declares
 * `double AreaAboveGround/AreaBelowGround` and `int BasementFloors/FloorsAboveGround`, and
 * `CreateOrUpdateHeatAreaRequest` declares `double HeatedFloorAreaAboveGround/HeatedBasementArea` —
 * all six non-nullable, backing the same NOT NULL columns `buildingAreaRows` explains. A null in any
 * of them is not a value the API stores as "unset", it is a payload System.Text.Json refuses, so the
 * whole PUT answers 400 and every section riding on it fails with it.
 *
 * Which is how below ground stopped accepting 0 or blank (RTLCA-5825). Below ground is optional —
 * `validateBuildingDetails` passes a null `areaBelowGround`, and its floors and heated area with it —
 * so an unanswered basement reaches the writes as three nulls. On the Hasura rail that is fine, since
 * `buildingAreaRows` coerces them. But entering an operational (B6) or construction-site (A5) energy
 * value changes `buildRestOnlyAssumptions`, which forces the PUT for the whole assumptions record and
 * takes the building areas with it — the one path where those nulls go on the wire. Hence: the same
 * `?? 0` on both rails, so which rail carries a save can never change what it stores.
 *
 * Everything else on `referencedAreas` is passed through untouched. `values` and `entries[].area` are
 * nullable in the wizard's own model on purpose — a blank reference area is an unanswered question
 * that `validateReferenceArea` refuses, not a zero — so coercing those would invent an answer.
 */
export const referencedAreasAsStored = (referencedAreas: ReferencedAreasType): ReferencedAreasType => ({
  ...referencedAreas,
  areaAboveGround: referencedAreas.areaAboveGround ?? 0,
  floorsAboveGround: referencedAreas.floorsAboveGround ?? 0,
  areaBelowGround: referencedAreas.areaBelowGround ?? 0,
  basementFloors: referencedAreas.basementFloors ?? 0,
});

/** The heated half of the same rule — they are columns of the same two rows. */
export const heatAreaAsStored = (heatArea: HeatAreaType | null | undefined): HeatAreaType => ({
  ...heatArea,
  heatedFloorAreaAboveGround: heatArea?.heatedFloorAreaAboveGround ?? 0,
  heatedBasementArea: heatArea?.heatedBasementArea ?? 0,
});

/**
 * Whether anyone has answered any of the six numbers yet.
 *
 * The columns are NOT NULL, so `buildingAreaRows` turns every unanswered field into a 0 and the rows
 * it builds cannot tell "the reader typed 0" from "the reader typed nothing". That is fine for a save
 * of what somebody filled in, and wrong for a project nobody has filled in at all: writing the rows
 * then stores a 0 m² building with 0 floors, which reads back as an ANSWER — the settings page shows
 * six zeros, the Building area section validates (`isNonNegative(0)`), and the per-m² result divides
 * by the area someone is about to be told is already set (RTLCA-5784).
 *
 * No rows is the honest representation of that state, and the one the wrapper already reads as null
 * (`projectWrapperFromHasura`: `buildingAreas.find(...)` returns nothing) — so the fields open blank,
 * which is what they are.
 *
 * `!= null` rather than truthiness, for the same reason the rest of this rail spells it out: 0 is an
 * answer.
 */
export const hasAnyBuildingArea = (input: ProjectBuildingAreasWriteInput): boolean =>
  isMixedUse(input.uses) ||
  [
    input.areaAboveGround,
    input.floorsAboveGround,
    input.heatedAreaAboveGround,
    input.areaBelowGround,
    input.basementFloors,
    input.heatedBasementArea,
  ].some((value) => value != null);

interface BuildingAreaField {
  field: string;
  unit?: string;
  value: number;
}

/**
 * The six fields as the user names them, carrying the value that is actually WRITTEN — read off the
 * rows, so the NOT NULL coercion has already happened. That is what keeps the trail honest: a field
 * that moved from null to 0 stored the same number it already held, and logging it as a change would
 * describe an edit nobody made.
 */
const writtenFields = (input: ProjectBuildingAreasWriteInput): BuildingAreaField[] => {
  // The project-level figures, which for a mixed-use project are the sums the table wrote onto it.
  const [aboveGround, belowGround] = projectRows(input);
  return [
    { field: "Area above ground", unit: "m²", value: aboveGround.area },
    { field: "Floors above ground", value: aboveGround.floors },
    { field: "Heated area above ground", unit: "m²", value: aboveGround.heatedArea },
    { field: "Area below ground", unit: "m²", value: belowGround.area },
    { field: "Basement floors", value: belowGround.floors },
    { field: "Heated basement area", unit: "m²", value: belowGround.heatedArea },
  ];
};

/**
 * The trail row. "project", not a new entity type: project_version_entity_type_check is a Postgres
 * CHECK constraint, and the building areas are project base information — filed under the Base info
 * chip beside the calculation, the address and the reference areas.
 *
 * The summary must never contain the word "revision": isRevisionEvent in projectVersion.types.ts is
 * `entityType === "project" && /\brevisions?\b/i.test(summary)`, so a stray mention would file the row
 * under the Team chip instead.
 *
 * One change per field whose written value moved; with no baseline all six are listed with an unknown
 * `from`. No `unit` on the two floor counts — they are not areas.
 */
export const buildBuildingAreasVersionInput = (
  input: ProjectBuildingAreasWriteInput,
  previousInput: ProjectBuildingAreasWriteInput | null,
  labelFor: (buildingTypeId: number) => string = (buildingTypeId) => String(buildingTypeId)
): ProjectVersion_Insert_Input => {
  const next = writtenFields(input);
  const previous = previousInput === null ? null : writtenFields(previousInput);

  // Paired by index before filtering, because `writtenFields` returns the same six fields in the same
  // order on both sides — the filtered list has no index to look a baseline up by.
  const changes: { field: string; from: string | null; to: string | null; unit?: string }[] = next
    .map((entry, index) => ({ entry, previousValue: previous === null ? null : previous[index].value }))
    // A null `previousValue` is "no baseline", which never equals a written number, so all six list.
    .filter(({ entry, previousValue }) => previousValue !== entry.value)
    .map(({ entry, previousValue }) => ({
      field: entry.field,
      from: previousValue === null ? null : String(previousValue),
      to: String(entry.value),
      ...(entry.unit === undefined ? {} : { unit: entry.unit }),
    }));

  // The split by use, listed only when it moved; a project that stays single-use has none.
  const usesFrom = previousInput === null ? null : usesText(previousInput.uses, labelFor);
  const usesTo = usesText(input.uses, labelFor);
  const usesChanged = previousInput === null ? isMixedUse(input.uses) : usesFrom !== usesTo;
  if (usesChanged) changes.push({ field: BUILDING_USES_FIELD, from: usesFrom, to: usesTo });

  const single = changes.length === 1 ? changes[0] : null;

  return {
    projectId: input.projectId,
    entityType: "project",
    operation: "update",
    // The field named on its own rather than wrapped in a "Building area "…"" phrase like the
    // reference areas: two of these six are floor COUNTS, and calling a floor count a building area
    // in the history would simply be wrong.
    summary: single ? `${single.field} updated` : usesChanged ? "Building uses updated" : "Building areas updated",
    entityLabel: single?.field ?? null,
    changes,
  };
};

const BUILDING_USES_FIELD = "Building uses";

const usesText = (uses: BuildingUseType[] | undefined, labelFor: (buildingTypeId: number) => string): string => {
  if (!isMixedUse(uses)) return "Single use";
  return byGrossAreaDescending(uses)
    .map(
      (use) =>
        `${use.buildingTypeId === null ? "?" : labelFor(Number(use.buildingTypeId))} ${use.areaAboveGround ?? 0}/${
          use.areaBelowGround ?? 0
        } m²`
    )
    .join(", ");
};
